import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Error from '@material-ui/icons/Error';
import Typography from '@material-ui/core/Typography';

import { validateFieldData } from '../../Utils/Validator';
import ActionButton from '../ActionButton';
import MenuTitleBar from '../MenuTitleBar';
import { useDispatch, useSelector } from 'react-redux';
import { resetConfigurationCode, saveConfiguration } from '../../Reducers/Configuration/Actions';
import { ConfigurationContext } from '../../ContextProviders/ConfigurationProvider';
import { VisualizationContext } from '../../ContextProviders/VisualizationProvider';
import { resetError, resetRequestOffer, sendRequestOffer } from '../../Reducers/Offer/Actions';
import { useForm } from '../../Hooks/useForm';
import { withLocalValue } from '../withLocalValue';
import PrivacyPolicyControl from '../ConfigurationToolbar/PrivacyPolicyControl';
import { ActivePopupContext } from '../../ContextProviders/ActivePopupProvider';
import ButtonLoader from '../ButtonLoader';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  FormGroup,
  Checkbox,
} from '@material-ui/core';
import { useComponentOptions } from '../../Hooks/useComponentOptions';

const StyledTextField = withStyles({
  root: {
    marginBottom: 8,
  },
})(withLocalValue(TextField));

const useStyles = makeStyles(() => ({
  actions: {
    padding: 24,
  },
  errorResult: {
    background: '#ED1C29',
    color: '#fff',
    padding: '20px',
    borderRadius: '4px',
    fontSize: 16,
    lineHeight: 1.7,
    marginBottom: 16,
    textAlign: 'center',
    '& p': { color: '#fff !important' },
  },
  errorResultIcon: {
    float: 'left',
  },
  fieldset: {
    display: 'flex',
    marginTop: 16,
  },
  requiredFieldMessage: {
    flex: 1,
  },
  checkbox: {
    color: 'grey',
  },
  firstLabel: {
    fontWeight: 'bold',
    marginTop: '5px',
    marginBottom: '0px',
  },
  boldLabel: {
    fontWeight: 'bold',
    marginTop: '25px',
    marginBottom: '0px',
  },
  label: {
    marginTop: '15px',
    marginBottom: '0px',
  },
  input: {
    '& .MuiFormLabel-root': {
      width: '95%',
      fontSize: '14px',
    },
  },
}));

const tentsFormFields = {
  tentsYourProject: {
    type: 'label',
    titleKey: 'tentsYourProject',
    makeBold: true,
    firstLabel: true,
  },
  tentsPurpose: {
    type: 'label',
    titleKey: 'tentsPurpose',
  },
  purposeBox: {
    type: 'text',
    multiline: true,
    minRows: 3,
    maxRows: 6,
    value: '',
    emptyLabel: true,
  },
  tentsLocation: {
    type: 'label',
    titleKey: 'tentsLocation',
  },
  tentsLocationText: {
    type: 'text',
    multiline: true,
    minRows: 3,
    maxRows: 6,
    value: '',
    makeSmaller: true,
    required: true,
    longText: true,
  },
  installationSite: {
    type: 'label',
    titleKey: 'installationSite',
    makeBold: true,
  },
  plannedStreet: {
    type: 'text',
    required: true,
    value: '',
  },
  plannedCity: {
    type: 'text',
    required: true,
    value: '',
  },
  plannedPostalCode: {
    type: 'text',
    required: true,
    value: '',
  },
  contactData: {
    type: 'label',
    titleKey: 'contactData',
    makeBold: true,
  },
  surname: {
    type: 'text',
    required: true,
    value: '',
  },
  firstname: {
    type: 'text',
    required: true,
    value: '',
  },
  company: {
    type: 'text',
    required: true,
    value: '',
  },
  phoneNumber: {
    type: 'tel',
    required: true,
    value: '',
  },
  emailAddress: {
    type: 'email',
    required: true,
    value: '',
  },
  street: {
    type: 'text',
    required: true,
    value: '',
  },
  zip: {
    type: 'text',
    required: true,
    value: '',
  },
  city: {
    type: 'text',
    required: true,
    value: '',
  },
  country: {
    type: 'text',
    required: true,
    value: '',
  },
  message: {
    type: 'text',
    multiline: true,
    minRows: 3,
    maxRows: 6,
    value: '',
    makeSmaller: true,
    longText: true,
  },
};

const warehouseFormFields = {
  yourProject: {
    type: 'label',
    titleKey: 'yourProject',
    makeBold: true,
    firstLabel: true,
  },
  purpose: {
    type: 'label',
    titleKey: 'purpose',
  },
  purposeBox: {
    type: 'text',
    multiline: true,
    minRows: 3,
    maxRows: 6,
    value: '',
    emptyLabel: true,
    required: true,
  },
  location: {
    type: 'label',
    titleKey: 'location',
  },
  locationText: {
    type: 'text',
    multiline: true,
    minRows: 3,
    maxRows: 6,
    value: '',
    makeSmaller: true,
    longText: true,
    required: true,
  },
  plannedCity: {
    type: 'text',
    required: true,
    value: '',
  },
  plannedPostalCode: {
    type: 'text',
    required: true,
    value: '',
  },
  isRental: {
    type: 'checkboxes',
    value: {
      purchase: false,
      rental: false,
      installmentPlan: false,
    },
    options: ['purchase', 'rental', 'installmentPlan'],
    allowedItems: ['warehouse'],
  },
  additionalEquipment: {
    type: 'checkboxes',
    value: [],
    options: [],
  },
  contactData: {
    type: 'label',
    titleKey: 'contactData',
    makeBold: true,
  },
  surname: {
    type: 'text',
    required: true,
    value: '',
  },
  firstname: {
    type: 'text',
    required: true,
    value: '',
  },
  company: {
    type: 'text',
    required: true,
    value: '',
  },
  phoneNumber: {
    type: 'tel',
    required: true,
    value: '',
  },
  emailAddress: {
    type: 'email',
    required: true,
    value: '',
  },
  street: {
    type: 'text',
    required: true,
    value: '',
  },
  zip: {
    type: 'text',
    required: true,
    value: '',
  },
  city: {
    type: 'text',
    required: true,
    value: '',
  },
  country: {
    type: 'text',
    required: true,
    value: '',
  },
  message: {
    type: 'text',
    multiline: true,
    minRows: 3,
    maxRows: 6,
    value: '',
    makeSmaller: true,
    longText: true,
  },
};

const StyledDialogContent = withStyles({
  root: {
    padding: '24px 24px 24px 24px',
  },
})(DialogContent);

const RequestOffer = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { loadCode } = useSelector((state) => state.Configuration);
  const { error, requestOfferReceived } = useSelector((state) => state.Offer);
  const visualization = useContext(VisualizationContext);
  const customData = useContext(ConfigurationContext);
  const itemIdentifier = useSelector((state) => state.Configuration.configuration?.item?.identifier);
  const isTent = itemIdentifier === 'tent';
  const { formData, changeHandler, debouncedChangeHandler, validate, reset, getOfferRequestValues } = useForm(
    isTent ? tentsFormFields : warehouseFormFields,
    {
      validatorFn: validateFieldData,
    }
  );
  const [privacyPolicyApproved, setPrivacyPolicyApproved] = useState(false);

  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [configurationReady, setConfigurationReady] = useState(false);

  const { t } = useTranslation();

  const additionalEquipmentOptions = useComponentOptions('additional_equipment');

  useEffect(() => {
    if (!isTent) {
      formData.additionalEquipment.componentOptions = additionalEquipmentOptions;
    }
  }, [formData, additionalEquipmentOptions, isTent]);

  useEffect(() => {
    if (requestOfferReceived && !error) {
      setSuccess(true);
      dispatch(resetRequestOffer());
      reset();
    }
  }, [dispatch, error, requestOfferReceived, reset]);

  const handleClickOpen = async () => {
    setPrivacyPolicyApproved(false);
    await dispatch(resetError());
    await dispatch(resetConfigurationCode());
    setSuccess(false);
    setOpen(true);
    setConfigurationReady(false);
    const screenshots = (await visualization.getScreenshots()) || {};
    const data = customData.getData();
    try {
      await dispatch(saveConfiguration(data, screenshots, 'user'));
    } finally {
      setConfigurationReady(true);
    }
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    dispatch(resetRequestOffer());
    reset();
  }, [dispatch, reset]);

  useContext(ActivePopupContext).useActive('RequestOffer', handleClose, open);

  const sendButtonClickHandler = () =>
    validate() && dispatch(sendRequestOffer(getOfferRequestValues(loadCode)));

  const dialogContent = success ? (
    <>
      <MenuTitleBar title={t('requestOffer.success.dialogTitle')} onClose={handleClose} />
      <StyledDialogContent>
        <Typography>{t('requestOffer.success.text')}</Typography>
      </StyledDialogContent>

      <DialogActions className={classes.actions}>
        <ActionButton
          label={t('requestOffer.success.okButtonText')}
          onClick={handleClose}
          disabled={!loadCode}
        />
      </DialogActions>
    </>
  ) : (
    <>
      <MenuTitleBar title={t('requestOffer.dialogTitle')} onClose={handleClose} />
      <StyledDialogContent>
        {error && (
          <div className={classes.errorResult}>
            <Error className={classes.errorResultIcon} />
            <Typography variant="body1">{t('requestOffer.loadError')}</Typography>
          </div>
        )}
        {Object.keys(formData).map((name) => {
          switch (formData[name].type) {
            case 'radioSwitch': {
              return (
                <FormControl component="fieldset" key={name}>
                  <FormLabel component="legend">{t(`requestOffer.fields.${name}.label`)}</FormLabel>
                  <RadioGroup
                    name={name}
                    value={formData[name].value}
                    onChange={() => changeHandler(name, !formData[name].value)}
                  >
                    {formData[name].options.map(({ value, label }) => (
                      <FormControlLabel
                        key={value}
                        value={value}
                        control={<Radio />}
                        label={t(`requestOffer.fields.${name}.${label}`)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              );
            }
            case 'checkboxes': {
              if (!formData[name].componentOptions && !formData[name].options) {
                return null;
              }
              return (
                <FormControl component="fieldset" key={name} className={classes.fieldset}>
                  <FormLabel component="legend">{t(`requestOffer.fields.${name}.label`)}</FormLabel>
                  {formData[name].options && (
                    <FormGroup>
                      {formData[name].options.map((optionName) => (
                        <FormControlLabel
                          name={optionName}
                          onChange={() =>
                            changeHandler(name, {
                              ...formData[name].value,
                              [optionName]: !formData[name].value[optionName],
                            })
                          }
                          key={optionName}
                          control={
                            <Checkbox
                              checked={formData[name].value[optionName]}
                              className={classes.checkbox}
                            />
                          }
                          label={t(`requestOffer.fields.${name}.${optionName}`)}
                          className={classes.checkbox}
                        />
                      ))}
                    </FormGroup>
                  )}
                  {formData[name].componentOptions && (
                    <FormGroup>
                      {formData[name].componentOptions.map((option) => {
                        const checked = formData[name].value.some(
                          ({ identifier }) => identifier === option.identifier
                        );
                        return (
                          <FormControlLabel
                            name={option.title}
                            onChange={() =>
                              changeHandler(
                                name,
                                checked
                                  ? formData[name].value.filter(
                                      ({ identifier }) => identifier !== option.identifier
                                    )
                                  : [...formData[name].value, option]
                              )
                            }
                            key={option.identifier}
                            control={<Checkbox checked={checked} />}
                            label={option.title}
                            className={classes.checkbox}
                          />
                        );
                      })}
                    </FormGroup>
                  )}
                </FormControl>
              );
            }
            case 'label': {
              return (
                <React.Fragment key={name}>
                  {formData[name].titleKey && (
                    <FormLabel
                      component="h1"
                      className={
                        formData[name].makeBold
                          ? formData[name].firstLabel
                            ? classes.firstLabel
                            : classes.boldLabel
                          : classes.label
                      }
                    >
                      {t(`requestOffer.${formData[name].titleKey}`)}
                      {formData[name].required && ' *'}
                    </FormLabel>
                  )}
                  {formData[name].textKey && (
                    <Typography variant="body2">{t(`requestOffer.${formData[name].textKey}`)}</Typography>
                  )}
                </React.Fragment>
              );
            }
            default: {
              const { emptyLabel, makeSmaller, longText, ...otherFieldProps } = formData[name];
              return (
                <StyledTextField
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  key={name}
                  name={name}
                  {...otherFieldProps}
                  label={emptyLabel ? '' : t(`requestOffer.fields.${name}`)}
                  inputProps={longText ? { style: { marginTop: '15px' } } : {}}
                  InputLabelProps={{ root: { width: '90%' } }}
                  helperText={formData[name].error && t(`validationErrorTexts.${formData[name].helperText}`)}
                  onChange={debouncedChangeHandler}
                  onBlur={changeHandler}
                  className={makeSmaller ? classes.input : ''}
                />
              );
            }
          }
        })}
        <PrivacyPolicyControl onChange={setPrivacyPolicyApproved} checked={privacyPolicyApproved} />
      </StyledDialogContent>

      <DialogActions className={classes.actions}>
        <Typography variant="body2" className={classes.requiredFieldMessage}>
          * {t('requestOffer.requiredFieldMessage')}
        </Typography>
        <ActionButton
          label={
            <>
              <ButtonLoader show={!configurationReady} />
              {t('requestOffer.sendButtonText')}
            </>
          }
          onClick={sendButtonClickHandler}
          disabled={!configurationReady || !privacyPolicyApproved}
        />
      </DialogActions>
    </>
  );

  return (
    <>
      <ActionButton label={t('requestOffer.actionButtonText')} onClick={handleClickOpen} />

      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        {dialogContent}
      </Dialog>
    </>
  );
};

export default RequestOffer;
